import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { Intercom, shutdown } from '@intercom/messenger-js-sdk';
import { User } from 'avn/models/user';
import { AuthAppService } from 'avn/services/auth-app/auth-app.service';
import { Constants } from 'avn/services/constants';
import { StoreService } from 'avn/services/store.service';
import { UserService } from 'avn/services/user.service';
import { SharedCompsService } from 'avn/shared/shared-comps.service';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss']
})
export class SignInComponent implements OnInit, OnDestroy {
  loading = false;
  hidePassword = true;
  wellbeingData: any;
  selectedTab = 'signin';
  loginType = Constants.LOGIN_TYPE;
  hmacHash = '';

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private api: UserService,
    private authService: AuthAppService,
    private snackBar: SharedCompsService,
    private auth: AuthService,
    private store: StoreService
  ) {}

  signInForm = this.fb.group({
    email: ['', [Validators.required]],
    password: ['', [Validators.required]]
  });

  redirectUrl: string;

  ngOnInit(): void {
    if (this.loginType === 'normal') {
      const sub1 = this.route.queryParams.subscribe((params: any) => {
        this.redirectUrl = params['redirect'];
      });

      this.subscriptions.push(sub1);
    } else {
      this.loginAuth0();
    }
  }

  loginAuth0() {
    this.auth.idTokenClaims$.subscribe((id: any) => {
      if (id && id.__raw) {
        shutdown();
        this.authService.setAuth(id.__raw);
        this.authService.setTrinityLoggedUser(id);
        this.loginAuth0Customer();
        this.hmacHash = this.store.generateHmac(id?.email, Constants.INTERCOM_CONFIG.secretKey);
        this.inItIntercom(id);
      } else {
        this.auth.isAuthenticated$.subscribe((au: any) => {
          if (!au) {
            this.auth.loginWithRedirect();
          }
        });
      }
    });
  }

  inItIntercom(user: any) {
    if (user) {
      Intercom({
        app_id: Constants.INTERCOM_CONFIG.appId,
        user_name: user?.name,
        email: user?.email,
        platform: user?.platform,
        user_hash: this.hmacHash
      });
    }
  }

  loginAuth0Customer() {
    this.authService.clientIdType = 'frontend';
    const session_path = localStorage.getItem('current_path');
    this.api.getConfigData().subscribe((response: any) => Constants.setRuntimeConfig(response));
    if (session_path) {
      this.router.navigate([session_path]);
    } else {
      this.router.navigate(['/'], { relativeTo: this.route });
    }
  }

  showPasswordToggle() {
    this.hidePassword = !this.hidePassword;
  }

  signInUser() {
    this.loading = true;
    const login_obj = { ...this.signInForm.value };
    this.loginCall({ email: login_obj.email, password: login_obj.password });
  }

  loginCall(obj: any) {
    const responseObs = this.api.login(obj).subscribe({
      next: (response) => {
        const decrypt_text = response;
        try {
          const resp = typeof decrypt_text === 'string' ? JSON.parse(decrypt_text) : decrypt_text;
          this.loginResponse(resp);
        } catch (error) {
          this.snackBar.showSnackBar('Error in Authentication. Please try again after sometime');
          this.authService.purgeAuth();
        }
      },
      error: (error) => {
        this.loading = false;
        let errormsg = '';
        if (error?.error?.message) {
          errormsg = error?.error?.message;
        } else {
          errormsg = 'Error in Authentication. Please try again after sometime';
        }
        this.snackBar.showSnackBar(errormsg);
        this.authService.purgeAuth();
      }
    });
    this.subscriptions.push(responseObs);
  }

  loginResponse(response: any) {
    if (response.status === 500) {
      this.loading = false;
      let errormsg = '';
      const idx = response.Message.indexOf('Your Account is Locked. Reset your Login using Forgot Password? link');
      if (idx !== -1) {
        errormsg = 'Your Account is locked. You can unlock by resetting your password';
      } else {
        errormsg = 'Incorrect email or password';
      }
      this.snackBar.showSnackBar(errormsg);
      this.authService.purgeAuth();
      return;
    }
    this.snackBar.showSnackBar('Login Successful', 5);
    this.successHandler(response);
    this.navigateToPage(response);
  }

  successHandler(response: any) {
    this.authService.setAuth(response?.auth_token);
    const user = <User>response?.user;
    user.businesses = response.header_detail.businesses;
    user.header_detail = response.header_detail;
    this.authService.setTrinityLoggedUser(user);
    if (response.header_detail) {
      this.wellbeingData = response.header_detail.wellbeing;
      this.authService.setWellbeingDetails(response.header_detail.wellbeing);
    }
    this.loading = false;
    this.snackBar.showSnackBar(`Welcome back <b>${user.first_name}</b>`, 5);
    this.router.navigate(['/']);
  }

  navigateToPage(response: any) {
    if (
      (response.user.is_owner || response.user.is_group_owner || response.user.is_practitioner) &&
      response.header_detail?.businesses?.length > 0
    ) {
      // business user
      const businesses = response.header_detail.businesses;
      const business_id = businesses[0].uuid;
      this.router.navigate(['/business/' + business_id]);
    } else {
      console.info(this.redirectUrl);
      if (this.redirectUrl) {
        this.router.navigate([this.redirectUrl]);
      } else {
        this.router.navigate(['/']);
      }
    }
    // if (response.user.is_wellbeing_owner) {
    //   // wellbeing user
    //   this.router.navigate(['/wellbeing']);
    // } else
  }

  subscriptions: any[] = [];
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      try {
        sub.unsubscribe();
      } catch (error) {
        console.info('');
      }
    });
  }
}
